// Engagement (contracts/lume.md §3): what players saw and did, per UTC day, across stories, brain cards, banners,
// announcements, pushes, Ask Lume, Focus and Review, against the previous period of the same length; pushes by
// kind with their open rate; the best content; and how often a push brought players back within 24 h. Every role
// reads. The range lives in the URL (?from&to, UTC dates; default the last 30 days).

import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { type Engagement, type EngagementDay, useEngagement } from "../api/lume";
import { ColumnChart, StackedColumnChart, type StackSeries } from "../components/charts/charts";
import { tableStyles } from "../components/table";
import { Card, EmptyState, ErrorState, KpiTile, Loading, PageHeader, Segmented, Skeleton, Stat, TextInput } from "../components/ui";
import { addDays, daysBetween, formatShortDate, todayUtc } from "../lib/dates";
import { fmtInt, fmtPct } from "../lib/format";
import e from "./engagement.module.css";
import styles from "./pages.module.css";

const MAX_DAYS = 180;
const PRESETS = [7, 30, 90] as const;

/** Push kinds (the head of notification_log.kind) in a fixed colour order; the rest fold into "Other". */
const KINDS: { kind: string; label: string; color: string }[] = [
  { kind: "lume", label: "Lume", color: "var(--color-avatar-orbs-1-1)" },
  { kind: "broadcast", label: "Announcements & lessons", color: "var(--color-avatar-orbs-2-1)" },
  { kind: "brain_card", label: "Brain card", color: "var(--color-avatar-orbs-0-1)" },
  { kind: "streak_at_risk", label: "Streak at risk", color: "var(--color-avatar-orbs-3-1)" },
  { kind: "nudge", label: "Daily nudge", color: "var(--color-avatar-orbs-4-1)" },
];
const OTHER = { kind: "other", label: "Other", color: "var(--color-text-quaternary)" };
const KIND_NAME: Record<string, string> = { support_reply: "Support reply", weekly_recap: "Weekly recap", friends_played: "Friends played", team_nudge: "Team nudge" };
const kindLabel = (k: string) => KINDS.find((x) => x.kind === k)?.label ?? KIND_NAME[k] ?? k.replace(/_/g, " ");
const slotOf = (k: string) => (KINDS.some((x) => x.kind === k) ? k : OTHER.kind);

const rate = (a: number, b: number) => (b > 0 ? a / b : null);

/** "+12%" / "−8%" / "×23" / "new" against the previous period, or null when there is nothing to compare. */
function delta(now: number, before: number | undefined): string | null {
  if (before === undefined) return null;
  if (before === 0) return now > 0 ? "new" : null;
  const d = (now - before) / before;
  if (Math.abs(d) < 0.005) return "flat";
  if (now / before >= 10) return `×${Math.round(now / before)}`;
  return `${d > 0 ? "+" : "−"}${Math.round(Math.abs(d) * 100)}%`;
}

/** A dot-matrix readout fits about four characters: 4,533 → "4.5" k, 18,272 → "18" k. */
function readout(n: number): { value: string; unit?: string } {
  if (n < 1000) return { value: String(n) };
  if (n < 10_000) return { value: (n / 1000).toFixed(1), unit: "k" };
  if (n < 1_000_000) return { value: String(Math.round(n / 1000)), unit: "k" };
  return { value: (n / 1_000_000).toFixed(1), unit: "M" };
}

export function EngagementPage() {
  const [params, setParams] = useSearchParams();
  const today = todayUtc();
  const to = params.get("to") || today;
  const from = params.get("from") || addDays(to, -29);
  const span = daysBetween(from, to) + 1;
  const valid = from <= to && span <= MAX_DAYS + 1;
  const prevTo = addDays(from, -1);
  const prev = useEngagement({ from: addDays(prevTo, -(span - 1)), to: prevTo });
  const q = useEngagement({ from, to });
  const setRange = (f: string, t: string) => setParams(t === today && f === addDays(today, -29) ? {} : { from: f, to: t }, { replace: true });
  const preset = to === today ? PRESETS.find((p) => from === addDays(today, -(p - 1))) : undefined;

  const header = (
    <PageHeader
      title="Engagement"
      meta={
        <span className="mono">
          {formatShortDate(from)} – {formatShortDate(to)} UTC · {span} {span === 1 ? "day" : "days"} · compared with the {span} days before
        </span>
      }
      actions={
        <div className={e.range} data-testid="range">
          <Segmented
            label="Range"
            value={preset ? String(preset) : "custom"}
            onChange={(v) => v !== "custom" && setRange(addDays(today, -(Number(v) - 1)), today)}
            options={[...PRESETS.map((p) => ({ value: String(p), label: `${p} d` })), { value: "custom", label: "Custom", disabled: true }]}
          />
          <TextInput type="date" value={from} max={to} onChange={(ev) => ev.target.value && setRange(ev.target.value, to)} aria-label="From (UTC)" data-testid="range-from" />
          <span className={styles.faint}>–</span>
          <TextInput type="date" value={to} min={from} max={today} onChange={(ev) => ev.target.value && setRange(from, ev.target.value)} aria-label="To (UTC)" data-testid="range-to" />
        </div>
      }
    />
  );

  if (!valid) {
    return (
      <>
        {header}
        <ErrorState title="Pick another range" error={new Error(from > to ? "The start is after the end." : `At most ${MAX_DAYS} days at a time.`)} />
      </>
    );
  }
  if (q.isPending) {
    return (
      <>
        {header}
        <div className={styles.kpiRow}>
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={164} radius="30px" />
          ))}
        </div>
        <Loading rows={8} />
      </>
    );
  }
  if (q.isError) {
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );
  }
  return (
    <>
      {header}
      <Report data={q.data} prev={prev.data?.totals} days={span} />
    </>
  );
}

function Report({ data, prev, days }: { data: Engagement; prev: EngagementDay | undefined; days: number }) {
  const t = data.totals;
  const perDay = (v: number) => fmtInt(Math.round(v / Math.max(1, days)));
  const vs = (now: number, before: number | undefined) => {
    const d = delta(now, before);
    return d ? <span className={e.delta}>{d} vs before</span> : <span />;
  };
  return (
    <div className={e.stack}>
      <div className={styles.kpiRow} data-testid="kpis">
        <KpiTile
          palette="violet"
          label={
            <>
              Pushes <small>opened</small>
            </>
          }
          {...readout(t.pushes.opened)}
          foot={
            <>
              <span>
                {fmtPct(rate(t.pushes.opened, t.pushes.sent))} of {fmtInt(t.pushes.sent)} sent
              </span>
              {vs(t.pushes.opened, prev?.pushes.opened)}
            </>
          }
        />
        <KpiTile
          palette="azure"
          label={
            <>
              Stories <small>viewed</small>
            </>
          }
          {...readout(t.stories.views)}
          foot={
            <>
              <span>
                {fmtInt(t.stories.completed)} completed · {fmtPct(rate(t.stories.completed, t.stories.views))}
              </span>
              {vs(t.stories.views, prev?.stories.views)}
            </>
          }
        />
        <KpiTile
          palette="teal"
          label={
            <>
              Brain card <small>opens</small>
            </>
          }
          {...readout(t.brain.opens)}
          foot={
            <>
              <span>{perDay(t.brain.opens)} a day</span>
              {vs(t.brain.opens, prev?.brain.opens)}
            </>
          }
        />
        <KpiTile
          palette="ember"
          label={
            <>
              Ask Lume <small>questions</small>
            </>
          }
          {...readout(t.lume.questions)}
          foot={
            <>
              <span>{perDay(t.lume.questions)} a day</span>
              {vs(t.lume.questions, prev?.lume.questions)}
            </>
          }
        />
      </div>

      <div className={`${styles.statStrip} ${e.stats}`} data-testid="stats">
        <Stat label="Banner CTR" value={fmtPct(rate(t.banners.tapped, t.banners.shown), 1)} sub={`${fmtInt(t.banners.tapped)} taps of ${fmtInt(t.banners.shown)} shown`} />
        <Stat label="Announcements read" value={fmtInt(t.announcements.reads)} sub={`${fmtInt(t.announcements.taps)} button taps · ${fmtPct(rate(t.announcements.taps, t.announcements.reads))}`} />
        <Stat label="Focus attempts" value={fmtInt(t.focus.attempts)} sub={delta(t.focus.attempts, prev?.focus.attempts) ?? `${perDay(t.focus.attempts)} a day`} />
        <Stat label="Review cleared" value={fmtInt(t.review.cleared)} sub={delta(t.review.cleared, prev?.review.cleared) ?? `${perDay(t.review.cleared)} a day`} />
        <Stat label="Peak active" value={fmtInt(t.active)} sub="players on the busiest day" />
        <Stat label="Pushes sent" value={fmtInt(t.pushes.sent)} sub={`${perDay(t.pushes.sent)} a day`} />
      </div>

      <PushesCard data={data} />

      <div className={e.multiples} data-testid="series">
        <Series title="Active players" data={data} pick={(d) => d.active} unit="players" total={`peak ${fmtInt(t.active)}`} />
        <Series title="Stories viewed" data={data} pick={(d) => d.stories.views} unit="views" notes={(d) => [`${fmtInt(d.stories.completed)} completed`]} />
        <Series title="Brain card opens" data={data} pick={(d) => d.brain.opens} unit="opens" />
        <Series title="Ask Lume questions" data={data} pick={(d) => d.lume.questions} unit="questions" notes={(d) => [`${fmtInt(d.lume.players)} players`]} />
        <Series title="Focus attempts" data={data} pick={(d) => d.focus.attempts} unit="attempts" notes={(d) => [`${fmtInt(d.focus.players)} players`]} />
        <Series title="Review cleared" data={data} pick={(d) => d.review.cleared} unit="cleared" />
        <Series title="Announcement reads" data={data} pick={(d) => d.announcements.reads} unit="reads" notes={(d) => [`${fmtInt(d.announcements.taps)} button taps`]} />
        <Series
          title="Banner taps"
          data={data}
          pick={(d) => d.banners.tapped}
          unit="taps"
          notes={(d) => [`${fmtInt(d.banners.shown)} shown · ${fmtPct(rate(d.banners.tapped, d.banners.shown), 1)}`, `${fmtInt(d.banners.dismissed)} dismissed`]}
        />
      </div>

      <div className={e.tops}>
        <TopCard title="Top stories" testId="top-stories" empty="No story views in this range." headers={["Story", "Views", "Done", "Rate"]}>
          {data.top.stories.map((s) => (
            <tr key={s.id}>
              <td>
                <Link to={`/learn/${s.id}`} className={e.link}>
                  {s.title ?? "Untitled"}
                </Link>
              </td>
              <td data-align="right">{fmtInt(s.views)}</td>
              <td data-align="right">{fmtInt(s.completed)}</td>
              <td data-align="right">{fmtPct(s.completionRate)}</td>
            </tr>
          ))}
        </TopCard>
        <TopCard title="Top brain cards" testId="top-brain" empty="No brain card opens in this range." headers={["Card", "Opens"]}>
          {data.top.brainCards.map((c) => (
            <tr key={c.id}>
              <td>
                <Link to="/brain" className={e.link}>
                  {c.title ?? "Untitled"}
                </Link>
              </td>
              <td data-align="right">{fmtInt(c.opens)}</td>
            </tr>
          ))}
        </TopCard>
        <TopCard title="Top banners" testId="top-banners" empty="No banner taps in this range." headers={["Banner", "Shown", "Taps", "CTR"]}>
          {data.top.banners.map((b) => (
            <tr key={b.id}>
              <td>
                <Link to="/banners" className={e.link}>
                  {b.title ?? "Untitled"}
                </Link>
                <span className={e.sub}>{b.placement}</span>
              </td>
              <td data-align="right">{fmtInt(b.shown)}</td>
              <td data-align="right">{fmtInt(b.tapped)}</td>
              <td data-align="right">{fmtPct(b.ctr, 1)}</td>
            </tr>
          ))}
        </TopCard>
        <TopCard title="Top announcements" testId="top-announcements" empty="No announcement reads in this range." headers={["Announcement", "Reads", "Taps", "Rate"]}>
          {data.top.announcements.map((x) => (
            <tr key={x.id}>
              <td>
                <Link to="/notifications" className={e.link}>
                  {x.title}
                </Link>
              </td>
              <td data-align="right">{fmtInt(x.reads)}</td>
              <td data-align="right">{fmtInt(x.taps)}</td>
              <td data-align="right">{fmtPct(x.tapRate)}</td>
            </tr>
          ))}
        </TopCard>
      </div>

      <ReturnCard rows={data.pushReturn} />
    </div>
  );
}

// ---------------------------------------------------------------- pushes by kind

function PushesCard({ data }: { data: Engagement }) {
  const byKind = data.totals.pushes.byKind;
  const present = new Set(Object.keys(byKind).map(slotOf));
  const series: StackSeries[] = [...KINDS, OTHER].filter((k) => present.has(k.kind)).map((k) => ({ key: k.kind, label: k.label, color: k.color }));
  const rows = data.days.map((d) => {
    const values: Record<string, number> = {};
    for (const [k, v] of Object.entries(d.pushes.byKind)) values[slotOf(k)] = (values[slotOf(k)] ?? 0) + v.sent;
    return { key: d.date, label: formatShortDate(d.date), values, notes: [`${fmtInt(d.pushes.opened)} opened · ${fmtPct(rate(d.pushes.opened, d.pushes.sent))}`] };
  });
  const kinds = Object.entries(byKind).sort((a, b) => b[1].sent - a[1].sent);
  const colorOf = (k: string) => (KINDS.find((x) => x.kind === k) ?? OTHER).color;
  return (
    <Card title="Pushes sent, by kind" actions={<span className="mono">{fmtPct(rate(data.totals.pushes.opened, data.totals.pushes.sent))} opened overall</span>}>
      {data.totals.pushes.sent === 0 ? (
        <EmptyState title="No pushes in this range" />
      ) : (
        <div className={e.pushes}>
          <StackedColumnChart rows={rows} series={series} label="Pushes sent per day, by kind" valueName="sent" />
          <div className={tableStyles.wrap}>
            <table className={tableStyles.table} data-testid="push-kinds">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th data-align="right">Sent</th>
                  <th data-align="right">Opened</th>
                  <th data-align="right">Open rate</th>
                </tr>
              </thead>
              <tbody>
                {kinds.map(([k, v]) => (
                  <tr key={k} data-kind={k}>
                    <td>
                      <span className={e.kind}>
                        <span className={e.swatch} style={{ background: colorOf(k) }} aria-hidden />
                        {kindLabel(k)}
                      </span>
                    </td>
                    <td data-align="right" className="mono">
                      {fmtInt(v.sent)}
                    </td>
                    <td data-align="right" className="mono">
                      {fmtInt(v.opened)}
                    </td>
                    <td data-align="right" className="mono">
                      {fmtPct(rate(v.opened, v.sent), 1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

function ReturnCard({ rows }: { rows: Engagement["pushReturn"] }) {
  return (
    <Card title="Return after a push" actions={<span className={styles.faint}>players who used the app within 24 h</span>}>
      {rows.length === 0 ? (
        <EmptyState title="No pushes in this range" />
      ) : (
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table} data-testid="push-return">
            <thead>
              <tr>
                <th>Kind</th>
                <th data-align="right">Players</th>
                <th data-align="right">Returned</th>
                <th>Within 24 h</th>
              </tr>
            </thead>
            <tbody>
              {[...rows]
                .sort((a, b) => (b.rate ?? -1) - (a.rate ?? -1))
                .map((r) => (
                  <tr key={r.kind} data-kind={r.kind}>
                    <td>{kindLabel(r.kind)}</td>
                    <td data-align="right" className="mono">
                      {fmtInt(r.players)}
                    </td>
                    <td data-align="right" className="mono">
                      {fmtInt(r.returned)}
                    </td>
                    <td>
                      <span className={e.rateCell}>
                        <span className={e.rateBar}>
                          <span style={{ width: `${Math.round((r.rate ?? 0) * 100)}%` }} />
                        </span>
                        <span className="mono">{fmtPct(r.rate)}</span>
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- small multiples and top lists

function Series({
  title,
  data,
  pick,
  unit,
  notes,
  total,
}: {
  title: string;
  data: Engagement;
  pick: (d: EngagementDay) => number;
  unit: string;
  notes?: (d: EngagementDay) => string[];
  total?: string;
}) {
  const sum = data.days.reduce((s, d) => s + pick(d), 0);
  return (
    <Card title={title} actions={<span className="mono">{total ?? fmtInt(sum)}</span>}>
      <ColumnChart
        label={`${title} per day`}
        valueName={unit}
        height={120}
        rows={data.days.map((d) => ({ key: d.date, label: formatShortDate(d.date), value: pick(d), notes: notes?.(d) }))}
      />
    </Card>
  );
}

function TopCard({ title, testId, empty, headers, children }: { title: string; testId: string; empty: string; headers: string[]; children: ReactNode[] }) {
  return (
    <Card title={title} padded={false}>
      {children.length === 0 ? (
        <EmptyState title="Nothing yet">{empty}</EmptyState>
      ) : (
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table} data-testid={testId}>
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={h} data-align={i ? "right" : undefined}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{children}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
